import { useMemo, useState } from 'react';
import { ContentDocumentView } from '../components/content-document';
import { InterviewPractice } from '../components/interview-practice';
import { ReflectionForm } from '../components/reflection-form';
import { ErrorPage, LoadingPage } from '../components/resource-state';
import {
  loadInterviewBank,
  loadWeek,
  type Day,
  type InterviewBank,
  type Manifest,
  type Week
} from '../content/data';
import { useResource } from '../content/use-resource';
import { navigate } from '../navigation/routes';
import {
  dayCompletionStatus,
  firstIncompleteDayId,
  isDayUnlocked
} from '../progress/progress';
import { useProgress, useProgressActions } from '../progress/progress-context';

const stages = [
  { label: 'Learn', shortLabel: 'Learn' },
  { label: 'Mini project', shortLabel: 'Mini' },
  { label: 'Main project', shortLabel: 'Main' },
  { label: 'Check and reflect', shortLabel: 'Review' }
] as const;

function dayTime(day: Day): string {
  if (day.kind === 'saturday') return '8 to 10 hours';
  if (day.kind === 'sunday') return '60 to 90 minutes';
  return '4 to 6 hours';
}

function weekIdFromDay(dayId: string): string | null {
  return dayId.match(/^(week-\d{2})-day-\d{2}$/)?.[1] ?? null;
}

function moveToStage(dayId: string, stage: number, setStage: (dayId: string, stage: number) => void) {
  setStage(dayId, stage);
  requestAnimationFrame(() => {
    document.querySelector<HTMLElement>('#day-step-heading')?.focus();
  });
}

function WeekRail({ week, activeDayId, manifest }: { week: Week; activeDayId: string; manifest: Manifest }) {
  const progress = useProgress();
  const currentDayId = firstIncompleteDayId(manifest, progress);
  const completed = new Set(progress.completedDayIds);

  return (
    <aside className="week-rail" aria-label={`Week ${week.number} days`}>
      <div className="week-rail-heading">
        <span>Week {week.number}</span>
        <strong>{week.mainProject}</strong>
        <p>{week.milestone.replace(/^Milestone \d+:\s*/i, '')}</p>
      </div>
      <ol>
        {week.days.map((day) => {
          const done = completed.has(day.id);
          const current = currentDayId === day.id;
          const active = activeDayId === day.id;
          const accessible = done || current;
          const label = `${day.weekday}: ${day.title}`;

          return (
            <li className={`${done ? 'is-done' : ''} ${active ? 'is-active' : ''}`.trim()} key={day.id}>
              <span className="rail-marker" aria-hidden="true">{done ? '✓' : day.number}</span>
              {accessible ? (
                <a href={`#/day/${day.id}`} aria-current={active ? 'step' : undefined}>
                  <span>{day.weekday}</span>
                  <strong>{day.title}</strong>
                </a>
              ) : (
                <div aria-label={`${label}, not yet available`}>
                  <span>{day.weekday}</span>
                  <strong>{day.title}</strong>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <a className="rail-week-link" href={`#/week/${week.id}`}>Open the week overview</a>
    </aside>
  );
}

function StageNavigation({ dayId, stage }: { dayId: string; stage: number }) {
  const { setStage } = useProgressActions();

  return (
    <nav className="stage-nav" aria-label="Today's four steps">
      {stages.map((item, index) => (
        <button
          type="button"
          className={index === stage ? 'is-current' : index < stage ? 'is-past' : ''}
          aria-label={item.label}
          aria-current={index === stage ? 'step' : undefined}
          onClick={() => moveToStage(dayId, index, setStage)}
          key={item.label}
        >
          <span>{index + 1}</span>
          <strong className="stage-label-long">{item.label}</strong>
          <strong className="stage-label-short">{item.shortLabel}</strong>
        </button>
      ))}
    </nav>
  );
}

function StepFooter({ dayId, stage }: { dayId: string; stage: number }) {
  const { setStage } = useProgressActions();
  return (
    <div className="step-footer">
      {stage > 0 ? (
        <button
          className="secondary-button"
          type="button"
          onClick={() => moveToStage(dayId, stage - 1, setStage)}
        >
          Previous step
        </button>
      ) : <span />}
      {stage < stages.length - 1 ? (
        <button
          className="primary-button"
          type="button"
          onClick={() => moveToStage(dayId, stage + 1, setStage)}
        >
          Done with this step
        </button>
      ) : null}
    </div>
  );
}

function CompletionPanel({ day, isCurrent, isCompleted }: { day: Day; isCurrent: boolean; isCompleted: boolean }) {
  const progress = useProgress();
  const { completeDay } = useProgressActions();
  const [confirming, setConfirming] = useState(false);
  const status = dayCompletionStatus(day, progress);

  if (isCompleted) {
    return (
      <aside className="completion-panel completion-panel--done">
        <strong>Day closed</strong>
        <p>You can review the saved work here.</p>
        <a href="#/today">Return to your current day</a>
      </aside>
    );
  }

  if (!isCurrent) return null;

  const closeDay = () => {
    completeDay(day.id);
    setConfirming(false);
    navigate({ page: 'today' });
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  return (
    <aside className="completion-panel" aria-labelledby="close-day-heading">
      <h2 id="close-day-heading">Close this day</h2>
      <p>Your next day opens only after these gates pass.</p>
      <dl className="gate-counts">
        <div>
          <dt>Pass checklist</dt>
          <dd>{status.checklistDone} / {status.checklistTotal}</dd>
        </div>
        <div>
          <dt>Questions attempted</dt>
          <dd>{status.questionsDone} / {status.questionsTotal}</dd>
        </div>
        <div>
          <dt>Core reflections</dt>
          <dd>{status.reflectionsDone} / {status.reflectionsTotal}</dd>
        </div>
      </dl>

      {confirming ? (
        <div className="completion-confirmation" role="group" aria-label="Confirm day completion">
          <p>Close {day.weekday} and move your Today page forward?</p>
          <div>
            <button className="secondary-button" type="button" onClick={() => setConfirming(false)}>Keep working</button>
            <button className="primary-button" type="button" onClick={closeDay}>Mark day complete</button>
          </div>
        </div>
      ) : (
        <button
          className="primary-button full-width"
          type="button"
          disabled={!status.ready}
          onClick={() => setConfirming(true)}
        >
          Review completion
        </button>
      )}
      {!status.ready ? <p className="field-hint">Finish the missing items before closing the day.</p> : null}

      <details className="recovery-shortcut">
        <summary>Taking longer than planned?</summary>
        <p>Stop at the hard time box. Record the blocker and keep this day open. Do not borrow from Sunday.</p>
        <a href="#/guide/doc-00-orientation-recovery">Open the recovery guide</a>
      </details>
    </aside>
  );
}

function StageContent({
  stage,
  day,
  week,
  bank,
  manifest,
  readOnly
}: {
  stage: number;
  day: Day;
  week: Week;
  bank: InterviewBank;
  manifest: Manifest;
  readOnly: boolean;
}) {
  const questionById = useMemo(() => new Map(bank.questions.map((question) => [question.id, question])), [bank.questions]);
  const questions = day.questionIds.flatMap((id) => {
    const question = questionById.get(id);
    return question ? [question] : [];
  });
  const reflectionBlockIds = [...new Set(day.reflectionFields.map((field) => field.blockId))];

  if (stage === 0) {
    return (
      <>
        <h2 id="day-step-heading" tabIndex={-1}>Learn and test the model</h2>
        <ContentDocumentView
          document={day.documents.learn}
          manifest={manifest}
          excludeHeadings={['Interview questions', 'Next']}
          readOnly={readOnly}
        />
      </>
    );
  }

  if (stage === 1) {
    return (
      <>
        <h2 id="day-step-heading" tabIndex={-1}>{day.miniProjectTitle}</h2>
        <ContentDocumentView document={day.documents.miniProject} manifest={manifest} excludeHeadings={['Next']} readOnly={readOnly} />
      </>
    );
  }

  if (stage === 2) {
    return (
      <>
        <h2 id="day-step-heading" tabIndex={-1}>{week.mainProject}: today's slice</h2>
        <ContentDocumentView document={day.documents.mainIntegration} manifest={manifest} excludeHeadings={['Next']} readOnly={readOnly} />
      </>
    );
  }

  return (
    <>
      <h2 id="day-step-heading" tabIndex={-1}>Check your understanding</h2>
      <p className="step-intro">Answer aloud without notes. Mark the attempt before comparing it with the model answer.</p>
      <InterviewPractice questions={questions} readOnly={readOnly} />
      <ContentDocumentView
        document={day.documents.feedback}
        manifest={manifest}
        excludeHeadings={['Model answers']}
        excludeBlockIds={reflectionBlockIds}
        readOnly={readOnly}
      />
      <ReflectionForm day={day} readOnly={readOnly} />
    </>
  );
}

function DayWorkspace({
  manifest,
  week,
  bank,
  dayId
}: {
  manifest: Manifest;
  week: Week;
  bank: InterviewBank;
  dayId: string;
}) {
  const progress = useProgress();
  const day = week.days.find((candidate) => candidate.id === dayId);
  const currentDayId = firstIncompleteDayId(manifest, progress);

  if (!day) {
    return (
      <main className="state-page" id="main-content">
        <h1>That day is not in this build</h1>
        <a href="#/today">Open your current day</a>
      </main>
    );
  }

  const stage = progress.stageByDay[day.id] ?? 0;
  const isCompleted = progress.completedDayIds.includes(day.id);
  const isCurrent = currentDayId === day.id;

  return (
    <main className="day-layout" id="main-content">
      <WeekRail week={week} activeDayId={day.id} manifest={manifest} />

      <div className="day-workspace">
        <header className="day-heading">
          <div>
            <p>Week {week.number}, {day.weekday}</p>
            <h1>{day.title}</h1>
          </div>
          <dl>
            <div>
              <dt>Time box</dt>
              <dd>{dayTime(day)}</dd>
            </div>
            <div>
              <dt>Day type</dt>
              <dd>{day.kind === 'weekday' ? 'Build day' : day.kind === 'saturday' ? 'Milestone day' : 'Review day'}</dd>
            </div>
          </dl>
        </header>

        {!isCurrent && isCompleted ? <p className="review-banner">Completed day. Answers and pass gates are read-only.</p> : null}
        <StageNavigation dayId={day.id} stage={stage} />

        <article className="step-sheet">
          <StageContent stage={stage} day={day} week={week} bank={bank} manifest={manifest} readOnly={isCompleted} />
          <StepFooter dayId={day.id} stage={stage} />
        </article>
      </div>

      <div className="day-side-column">
        <CompletionPanel day={day} isCurrent={isCurrent} isCompleted={isCompleted} />
      </div>
    </main>
  );
}

function UnlockedDayLoader({ manifest, dayId, weekId }: { manifest: Manifest; dayId: string; weekId: string }) {
  const resource = useResource(
    () => Promise.all([loadWeek(manifest, weekId), loadInterviewBank()]),
    [weekId]
  );

  if (resource.status === 'loading') return <LoadingPage label="Opening the day's work" />;
  if (resource.status === 'error') return <ErrorPage error={resource.error} retry={resource.retry} />;

  const [week, bank] = resource.data;
  return <DayWorkspace manifest={manifest} week={week} bank={bank} dayId={dayId} />;
}

function DayLoader({ manifest, dayId }: { manifest: Manifest; dayId: string }) {
  const progress = useProgress();
  const weekId = weekIdFromDay(dayId);

  if (!weekId) {
    return (
      <main className="state-page" id="main-content">
        <h1>The day address is invalid</h1>
        <a href="#/today">Open your current day</a>
      </main>
    );
  }

  if (!isDayUnlocked(manifest, progress, dayId)) {
    return (
      <main className="state-page" id="main-content">
        <p className="status-word">Later in the sequence</p>
        <h1>Finish your current day first</h1>
        <p>You can preview the week overview, but daily work stays in dependency order.</p>
        <a className="primary-button" href="#/today">Return to today</a>
      </main>
    );
  }

  return <UnlockedDayLoader manifest={manifest} dayId={dayId} weekId={weekId} />;
}

function SequenceComplete({ manifest }: { manifest: Manifest }) {
  return (
    <main className="completion-page" id="main-content">
      <p className="status-word">Sequence complete</p>
      <h1>You closed all {manifest.totals.days} days.</h1>
      <p>Use the roadmap to review the evidence, project milestones, and interview answers you built along the way.</p>
      <div className="completion-links">
        <a className="primary-button" href="#/projects">Review projects</a>
        <a className="secondary-button" href="#/interview">Practice questions</a>
      </div>
    </main>
  );
}

export function TodayPage({ manifest, requestedDayId }: { manifest: Manifest; requestedDayId?: string }) {
  const progress = useProgress();
  const currentDayId = firstIncompleteDayId(manifest, progress);

  if (!currentDayId && !requestedDayId) return <SequenceComplete manifest={manifest} />;

  const dayId = requestedDayId ?? currentDayId;
  return dayId ? <DayLoader manifest={manifest} dayId={dayId} /> : <SequenceComplete manifest={manifest} />;
}
